import React, { useContext, memo } from 'react';
import { AppContext } from '@prisjakt/ui-app-context';
import { Panel, PanelHeader, PanelTitle } from '@prisjakt/ui/panel';
import { ParamsAndReturnTypes } from '@prisjakt/ui-translate/src/translate-func';
import { ProductRecommendation, RecommendedProduct } from 'src/types';
import { ModelType } from './types';
import { Link } from './styles';
import { trackProductRecommendationClicked } from './tracking';
import { ExpandableGrid } from 'src/components/expandable-grid';
import { ProductGridCard } from 'src/components/product-card-new';
import { useViewportContext } from 'src/components/viewport/context';
import {
  InternalNavigationScope,
  trackInternalNavigationClick,
} from 'src/globals/tracking/tracking';

const RecommendationModel: React.FC<OwnProps> = ({
  contextId,
  link,
  recommendations,
  type,
  background,
}) => {
  const { linkTo, t } = useContext(AppContext);
  const { isTablet } = useViewportContext();

  if (!recommendations?.length) {
    return null;
  }

  const handleProductClick = (product: RecommendedProduct, index: number) => {
    trackProductRecommendationClicked(contextId, product.id, type);
    trackInternalNavigationClick({
      scope: InternalNavigationScope.OTHER_VISITED_PRODUCTS_PRODUCT_CARD,
      nextPage: linkTo('product', product),
      index,
    });
  };

  const items = recommendations.map(({ product }, index) => {
    if (!product) return null;
    return (
      <ProductGridCard
        key={product.id}
        background={background}
        brand={product.brand?.name ? product.brand : undefined}
        category={product.category}
        imageSrc={product.media?.first}
        shouldLazyLoadImage
        link={linkTo('product', product)}
        name={product.name!}
        price={product.price?.regular}
        rating={product.userReviews?.rating}
        reviewsCount={product.userReviews?.count}
        onClick={() => handleProductClick(product, index)}
      />
    );
  });

  return (
    <Panel data-test-type="product-recommendations">
      <PanelHeader>
        <PanelTitle>
          {t(
            'product',
            `recommendations.${type as ModelType}` as keyof ParamsAndReturnTypes['product']
          )}
        </PanelTitle>
        {link && (
          <Link to={link.url} rel="nofollow" onClick={link?.onClick}>
            {link.title}
          </Link>
        )}
      </PanelHeader>

      <ExpandableGrid
        limit={isTablet ? 3 : 4}
        expandText={t('common', 'button.show-more')}
        collapseText={t('common', 'button.show-less')}
        cssGrid
        items={items}
      />
    </Panel>
  );
};

export default memo(RecommendationModel);

type LinkType = {
  title: string;
  url: string;
  onClick?: () => void;
};

type OwnProps = {
  contextId: string;
  link?: LinkType | null;
  recommendations: ProductRecommendation[];
  type: ModelType;
  background?: boolean;
};
